import {defineStore} from "pinia";
import {computed, ref} from "vue";
import {LocationQueryRaw, useRoute, useRouter} from "vue-router";
import * as YachtService from "@/services/yacht.service";
import {Yacht} from "@/models/Yacht";
import {useDestinationStore} from "@/stores/destinationStore";

export const useSearchStore = defineStore('searchStore', () => {

    const categoryOptions = ['Motor boat', 'Sailing yacht', 'Catamaran']

    const categories = ref<string[]>([]);
    // const destinations = ref<string[]>([]);
    const guests = ref(1);
    const minLength = ref(0);
    const maxLength = ref(100);
    const length = ref<[number, number]>([minLength.value, maxLength.value]);
    const minPrice = ref(0);
    const maxPrice = ref(250000);
    const price = ref<[number, number]>([minPrice.value, maxPrice.value]);

    const searchQuery = ref('');
    // const destinationStore = useDestinationStore();
    //
    // const destinationOptions = computed(() =>
    //     destinationStore.allDestinations.map(dest => dest.generalLocation)
    // );

    const isLengthFiltered = computed(() => {
        return length.value[0] !== minLength.value || length.value[1] !== maxLength.value;
    });

    const isPriceFiltered = computed(() => {
        return price.value[0] !== minPrice.value || price.value[1] !== maxPrice.value;
    });

    const router = useRouter();
    const route = useRoute();


    const dateFrom = ref<string | null>(null);
    const dateTo = ref<string | null>(null);

    const sortOptions = ['Popularity', 'Length (High)', 'Length (Low)', 'Price (High)', 'Price (Low)'];
    const selectedSort = ref(sortOptions[0]); // default option

    const loading = ref(false);
    const yachts = ref<Yacht[]>([]);

    const yachtsPerPage = 9;
    const currentPage = ref(1);


    const filteredYachts = computed(() => {
        let results = [...yachts.value];

        // Category
        if (categories.value.length > 0) {
            const selected = categories.value.map(c => c.trim().toLowerCase());

            results = results.filter(yacht => selected.includes(yacht.category));
        }

        // Guests
        results = results.filter(yacht => {
            const requiredBerths = Math.ceil(guests.value / 2);
            return yacht.berths >= requiredBerths;
        });

        // Length range
        results = results.filter(yacht => {
            return yacht.normalizedLength >= length.value[0] && yacht.normalizedLength <= length.value[1];
        });

        // Price range
        results = results.filter(yacht =>
            yacht.normalizedPrice >= price.value[0] && yacht.normalizedPrice <= price.value[1]
        );

        // Sorting
        switch (selectedSort.value) {
            case 'Price (Low)':
                results.sort((a, b) => a.normalizedPrice - b.normalizedPrice);
                break;
            case 'Price (High)':
                results.sort((a, b) => b.normalizedPrice - a.normalizedPrice);
                break;
            case 'Length (Low)':
                results.sort((a, b) => a.normalizedLength - b.normalizedLength);
                break;
            case 'Length (High)':
                results.sort((a, b) => b.normalizedLength - a.normalizedLength);
                break;
        }

        return results;
    });



    // Yachts to show on each page
    const paginatedYachts = computed(() => {
        const start = (currentPage.value - 1) * yachtsPerPage;
        return filteredYachts.value.slice(start, start + yachtsPerPage);
    });


    async function getAllYachts() {
        loading.value = true;
        try {
            const response = await YachtService.getYachts();

            if (!response) {
                console.error("Yachts not found");
                yachts.value = [];
                return null;
            }

            // Normalize lengthOverall and price to numbers once here
            yachts.value = response.data.map(yacht => ({
                ...yacht,
                normalizedLength: parseFloat(String(yacht.lengthOverall).replace(',', '.')) || 0,
                normalizedPrice: parseFloat(String(yacht.price).toString().replace(',', '.')) || 0,
                category: yacht.category?.trim().toLowerCase() || '',
            }));

            console.log("Yachts loaded and normalized:", yachts.value);
        } catch (error) {
            console.error("Error fetching yachts:", error);
            yachts.value = [];
        } finally {
            loading.value = false;
        }
    }


    const query = computed(() => {
        const q: LocationQueryRaw = {};

        if (categories.value.length > 0) q['categories'] = categories.value.join(',');
        // if (destinations.value.length > 0) q['destinations'] = destinations.value.join(',');
        if (dateFrom.value != null) q['dateFrom'] = dateFrom.value;
        if (dateTo.value !== null) q['dateTo'] = dateTo.value;
        if (length.value[0] > minLength.value || length.value[1] < maxLength.value) {
            q['lengthMin'] = length.value[0].toString();
            q['lengthMax'] = length.value[1].toString();
        }
        if (price.value [0] > minPrice.value || price.value[1] < maxPrice.value) {
            q['priceMin'] = price.value[0].toString();
            q['priceMax'] = price.value[1].toString();
        }
        if (guests.value) q['guests'] = guests.value.toString();
        if (selectedSort.value) q['sort'] = selectedSort.value;
        return q;
    })

    function updateRouteQuery() {
        router.replace({
            path: route.path,
            query: query.value,
        });
    }

    const clearAllFilters = () => {
        categories.value = []
        // destinations.value = []
        length.value = [minLength.value, maxLength.value]
        price.value = [minPrice.value, maxPrice.value]
        guests.value = 1
        // searchQuery.value = []
    }


    function setFromQuery(query: Record<string, any>) {
        categories.value = query.categories?.toString().split(',') || [];
        // destinations.value = query.destinations?.toString().split(',') || [];

        length.value =
            query.lengthMin && query.lengthMax
                ? [parseInt(query.lengthMin), parseInt(query.lengthMax)]
                : [minLength.value, maxLength.value];

        price.value =
            query.priceMin && query.priceMax
                ? [parseInt(query.priceMin), parseInt(query.priceMax)]
                : [minPrice.value, maxPrice.value];

        guests.value = query.guests ? parseInt(query.guests) : null;

        dateFrom.value = query.dateFrom || null;
        dateTo.value = query.dateTo || null;

        selectedSort.value = query.sort || sortOptions[0];

    }


    return {
        yachts,
        filteredYachts,
        loading,
        getAllYachts,
        categories,
        // destinations,
        length,
        price,
        guests,
        dateFrom,
        dateTo,
        query,
        minLength,
        maxLength,
        minPrice,
        maxPrice,
        setFromQuery,
        sortOptions,
        selectedSort,
        clearAllFilters,
        isLengthFiltered,
        isPriceFiltered,
        categoryOptions,
        // destinationOptions,
        paginatedYachts,
        yachtsPerPage,
        currentPage,
        updateRouteQuery,
        searchQuery
    }

})
